<template lang="html">
  <div class="image-upload" :class="{'image-upload--empty': !hasImage, 'image-upload--highlight': highlight, 'image-upload--small': variant == 'small'}">
    <div class="image-upload-image">
      <input
      @dragenter="handleDragDrop"
      @dragleave="handleDragDrop"
      @dragover.prevent
      @drop="handleDragDrop"
      class="image-upload__input"
      type="file"
      @change="onFileChange"
      ref="input"
      accept="image/png, image/jpeg, image/gif"/>
      <div v-if="!hasImage" class="image-upload__icon-container">
        <Transition name="pop" mode="out-in" v-if="!uploading">
            <img :key="icon" :src="icon" :class="['image-upload__icon--' + previewType]" class="image-upload__icon" aria-hidden="true"/>
        </Transition>
        <LoadingIcon v-else class="loader" :scaleTransition="false" :colours="['var(--spacein-yellow)', 'white']"/>
      </div>
      <div v-else class="image-upload-image-container">
        <img class="image-upload__preview-image" :src="previewImage" alt="Current image"/>
      </div>
    </div>
    <div class="image-upload-details">
      <p class="image-upload-details__header">
        <slot name="header"/>
      </p>
      <p class="image-upload-details__body">
        <slot name="body"/>
      </p>
      <div class="image-upload-details__actions">
        <span v-if="uploading" class="action action--uploading"></span>
        <span v-if="hasImage" @click="$emit('remove')" role="button" aria-label="Remove image" class="action action--remove">Remove</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { processMediaURL } from '@/functions/utility.js';
import LoadingIcon from '@/components/global/loading/LoadingIcon.vue';

export default {
  name: 'ImageUpload',
  data: function() {
    return {
      highlight: false,
      uploading: false
    }
  },
  components: {
    LoadingIcon
  },
  props: {
    imageURL: String,

    // possible values for type:
    // spaceBannerImage spaceCoverImage spaceLogoImage mediaItemImage
    // also userAvatar, but that's covered by the other endpoint
    imageType: String,

    // Normal, Full
    // Full is for backgrounds, and will stretch to the bounds of the components
    previewType: String,
    // Normal, small
    variant: String
  },
  computed: {
    ...mapGetters({
      isMobile: 'viewport/isMobile'
    }),
    hasImage: function() {
      return this.previewImage && this.previewImage != ""
    },
    icon: function() {
      return this.highlight ? require("@/assets/global-icons/success.png"): require("@/assets/global-icons/upload-arrow.svg")
    },
    previewImage: function() {
      if (this.uploading || this.highlight) {
        return ""
      }

      return processMediaURL(this.imageURL)
    },
    shouldShowMobilePlaceholder() {
      return this.isMobile && this.$slots['placeholder-mobile']
    },
    shouldShowDesktopPlaceholder() {
      if (this.isMobile) {
        return !this.$slots['placeholder-mobile']
      }

      return true
    }
  },
  watch: {
    imageURL: function(newImageURL) {
      if (!newImageURL || newImageURL == "") {
        // We do this to make sure the input value is cleared when the provided imageURL is cleared
        this.$refs.input.value = ""
      }
    }
  },
  methods: {
    ...mapActions(['api/media/uploadImage', 'popups/show/error/uploading']),
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;

      this.uploading = true
      let res = await this['api/media/uploadImage']([files[0], this.imageType])
      if(!res?.success) this['popups/show/error/uploading']('your Image')

      this.uploading = false

      if(res?.success) this.$emit('imageUploaded', res.success);
    },

    async handleDragDrop(e) {
      switch (e.type) {
        case 'dragenter':
          this.highlight = true;
          break;

        case 'dragleave':
        case 'drop':
          this.highlight = false;
          break;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.image-upload {
  box-sizing: border-box;
  display: flex;
  min-height: 160px;

  &-image {
    box-sizing: border-box;
    border: 3px solid $grey-3;
    width: 160px;
    height: 160px;
    border-radius: 25px;
    position: relative;
    flex-shrink: 0;

    overflow: hidden;
  }

  &-image-container {
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;

    &--full {
      width: 100%;
      height: 100%;
    }
  }

  &-details {
    padding-top: 5px;
    padding-bottom: 10px;
    margin-left: 20px;
    display: flex;
    flex-direction: column;

    &__header {
      color: white;
      font-weight: bold;
    }

    &__body {
      margin-top: 8px;
      color: $grey-2;
    }

    &__actions {
      margin-top: auto;
    }
  }

  &__icon-container {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    .loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &--empty {
    .image-upload-image {
      background-color: $grey-6;

      &:hover {
        background-color: $grey-4;
      }
    }
  }

  &--highlight {
    .image-upload-image {
      background-color: $grey-5;
    }
  }

  &__input {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    opacity: 0;
    cursor: pointer;
    appearance: none;
    z-index: 5;
    background-color: red;
    border-radius: 25px;
  }

  &--small {
    min-height: 80px;
    .image-upload-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
    }

    .image-upload-details {
      justify-content: center;
      padding: 0;
      margin-bottom: 15px;

      &__actions {
        margin-top: 5px;
      }
    }
  }
}

.action {
  cursor: pointer;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }

  &--remove {
    color: $red;
  }

  &--uploading {
    color: $grey-1;
    font-weight: normal;
  }
}

.pop-enter-active,
.pop-leave-active {
  transition: all 0.1s ease-out;
}

.pop-enter-from,
.pop-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

@import '~@/scss/components/global/loading/loading_screen.scss';
</style>
